import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { generateQuotePDF, downloadPDF } from '../../../utils/pdfGenerator';
import { supabase } from '../../../lib/supabase';

const QuoteDetailModal = ({ isOpen, onClose, quote, onOpenApproval }) => {
  const [orgSettings, setOrgSettings] = useState({});

  useEffect(() => {
    if (isOpen) {
      supabase?.from('organizations')?.select('*')?.limit(1)?.single()
        ?.then(({ data }) => { if (data) setOrgSettings(data); });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0)?.toFixed(2)}`;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'draft':
        return 'bg-muted text-muted-foreground border-border';
      case 'sent':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'approved':
        return 'bg-success/10 text-success border-success/20';
      case 'rejected':
        return 'bg-error/10 text-error border-error/20';
      case 'converted':
        return 'bg-secondary/10 text-secondary border-secondary/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const handleDownloadPDF = () => {
    const html = generateQuotePDF(quote, orgSettings);
    downloadPDF(html, `quote-${quote?.quote_number}.pdf`);
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-5 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-semibold text-foreground">Quote Details</h2>
              <p className="text-sm text-muted-foreground">{quote?.quote_number}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth"
          >
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-caption font-medium text-muted-foreground mb-2">Customer</h3>
              <p className="font-medium text-foreground">{quote?.customer?.name}</p>
              <p className="text-sm text-muted-foreground">{quote?.customer?.email}</p>
              <p className="text-sm text-muted-foreground">{quote?.customer?.phone}</p>
            </div>

            <div>
              <h3 className="text-sm font-caption font-medium text-muted-foreground mb-2">Salesperson</h3>
              <p className="font-medium text-foreground">{quote?.salesperson?.name || 'N/A'}</p>
              <p className="text-sm text-muted-foreground">{quote?.salesperson?.email || ''}</p>
            </div>

            <div>
              <h3 className="text-sm font-caption font-medium text-muted-foreground mb-2">Status</h3>
              <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium border ${getStatusColor(quote?.status?.name)}`}>
                {quote?.status?.name}
              </span>
            </div>

            <div>
              <h3 className="text-sm font-caption font-medium text-muted-foreground mb-2">Expected Service Date</h3>
              <p className="font-medium text-foreground">{formatDate(quote?.expected_service_date)}</p>
            </div>

            <div>
              <h3 className="text-sm font-caption font-medium text-muted-foreground mb-2">Created Date</h3>
              <p className="font-medium text-foreground">{formatDate(quote?.created_at)}</p>
            </div>

            {quote?.approval_date && (
              <div>
                <h3 className="text-sm font-caption font-medium text-muted-foreground mb-2">Approval Date</h3>
                <p className="font-medium text-foreground">{formatDate(quote?.approval_date)}</p>
              </div>
            )}
          </div>

          {quote?.notes && (
            <div>
              <h3 className="text-sm font-caption font-medium text-muted-foreground mb-2">Notes</h3>
              <p className="text-sm text-foreground bg-muted/30 rounded-lg p-4">{quote?.notes}</p>
            </div>
          )}

          <div>
            <h3 className="text-lg font-heading font-semibold text-foreground mb-4">Products & Services</h3>
            <div className="bg-background rounded-lg border border-border overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Product/Service</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Quantity</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Price</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {quote?.quote_products?.map((product) => (
                    <tr key={product?.id} className="border-b border-border last:border-0">
                      <td className="px-4 py-3 text-sm text-foreground">{product?.name}</td>
                      <td className="px-4 py-3 text-sm text-foreground">{product?.quantity}</td>
                      <td className="px-4 py-3 text-sm text-foreground">{formatCurrency(product?.price)}</td>
                      <td className="px-4 py-3 text-sm font-medium text-foreground">
                        {formatCurrency(product?.quantity * product?.price)}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-muted/30">
                    <td colSpan="3" className="px-4 py-3 text-right text-sm font-semibold text-foreground">
                      Total Amount:
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-primary">
                      {formatCurrency(quote?.total_amount)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border p-6 flex justify-end gap-2">
          {onOpenApproval && (
            <Button
              variant="outline"
              onClick={() => { onClose(); onOpenApproval(quote); }}
              iconName="GitBranch"
              iconPosition="left"
            >
              Approval Workflow
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleDownloadPDF}
            iconName="Download"
            iconPosition="left"
          >
            Download PDF
          </Button>
          <Button
            variant="outline"
            onClick={onClose}
            iconName="X"
            iconPosition="left"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuoteDetailModal;